/**
 * NinjaCash i18n singleton (F5 Language Plan Phase 0).
 *
 * Import this module EARLY (main.tsx does) so document.documentElement.lang
 * is set before first paint. Usage:
 *
 *   import { useLanguage } from '@/i18n';           // components
 *   const { t, language, setLanguage } = useLanguage();
 *   <h1>{t('settings.language.title')}</h1>
 *
 *   import { t } from '@/i18n';                     // non-React code
 *
 * Rules (LANGUAGE_NJCASH.md): per-device only, never synced; tests always
 * run in English and build label expectations FROM the en catalog import,
 * never from hardcoded strings.
 */

import { createI18n, LanguageDefinition } from './i18n';
import en from './catalogs/en.json';
import ja from './catalogs/ja.json';
import th from './catalogs/th.json';

export const LANGUAGES: LanguageDefinition[] = [
  { code: 'en', englishName: 'English', nativeName: 'English' },
  { code: 'ja', englishName: 'Japanese', nativeName: '日本語' },
  { code: 'th', englishName: 'Thai', nativeName: 'ไทย' },
];

// Vitest runs with DEV=true + jsdom localhost, so gate EVERYTHING that could
// make tests non-deterministic: no query override, no persistence reads/writes,
// no navigator detection -> tests are always plain English (same lesson as
// devAuth.ts MODE gate; ninjaedit's suite broke on language-of-the-day).
const IS_TEST = import.meta.env.MODE === 'test';

export const i18n = createI18n({
  languages: LANGUAGES,
  catalogs: { en, ja, th },
  defaultLanguage: 'en',
  fallbackLanguage: 'en',
  storageKey: IS_TEST ? '' : 'ninjacash_language',
  queryParam: IS_TEST ? false : 'lang',
  detectFromNavigator: !IS_TEST, // decision D2: first-run auto-detect
  // Forgiving ?lang= inputs (people type country codes / full names):
  aliases: { jp: 'ja', japanese: 'ja', thai: 'th', english: 'en' },
});

/** Translate outside React (services, non-component code). */
export const t = i18n.t.bind(i18n) as typeof i18n.t;

export { useLanguage } from './useLanguage';
export { categoryDisplayName } from './categoryNames';
export type { LanguageDefinition, TranslationParams, CatalogDiff } from './i18n';
export { compareCatalogs, flattenCatalog, interpolate, extractPlaceholders } from './i18n';
